import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTier } from '../../context/TierContext';
import { useTenantSettings } from '../../hooks/useTenantSettings';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Truck, Shield, Users, DollarSign, Lock, Factory, Wrench, Workflow, Calendar, Receipt, MapPin, Folder, Layers, Settings2, Ban } from 'lucide-react';

// Tab components
import AssetTypesTab from './AssetTypesTab';
import AssetMakesModelsTab from './AssetMakesModelsTab';
import AttachmentTypesTab from './AttachmentTypesTab';
import ComplianceTypesTab from './ComplianceTypesTab';
import WorkerTypesTab from './WorkerTypesTab';
import PriceListsTab from './PriceListsTab';
import MaterialTypesTab from './MaterialTypesTab';
import SetupProfilesTab from './SetupProfilesTab';
import LeaveTypesTab from './LeaveTypesTab';
import AllowanceTypesTab from './AllowanceTypesTab';
import TollsTab from './TollsTab';
import DepotsTab from './DepotsTab';
import ScheduleFoldersTab from './ScheduleFoldersTab';
import CancellationReasonsTab from './CancellationReasonsTab';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';

// Group definitions — each parent tab contains sub-tabs
const GROUPS = {
  assets: {
    label: 'Assets',
    icon: Truck,
    subtabs: [
      { key: 'equipment-types', label: 'Asset Types', icon: Truck, testId: 'tab-equipment-types' },
      { key: 'makes-models', label: 'Makes & Models', icon: Factory, testId: 'tab-makes-models' },
      { key: 'attachment-types', label: 'Attachment Types', icon: Wrench, testId: 'tab-attachment-types' },
    ],
  },
  workers: {
    label: 'Workers',
    icon: Users,
    subtabs: [
      { key: 'worker-types', label: 'Worker Types', icon: Users, testId: 'tab-worker-types', feature: 'worker_types' },
      { key: 'leave-types', label: 'Leave Types', icon: Calendar, testId: 'tab-leave-types', feature: 'worker_availability' },
      { key: 'allowance-types', label: 'Allowances', icon: DollarSign, testId: 'tab-allowance-types' },
    ],
  },
  compliance: {
    label: 'Compliance',
    icon: Shield,
    subtabs: [
      { key: 'compliance-types', label: 'Compliance Types', icon: Shield, testId: 'tab-compliance-types', feature: 'compliance' },
    ],
  },
  pricing: {
    label: 'Pricing & Billing',
    icon: DollarSign,
    subtabs: [
      { key: 'price-lists', label: 'Price Lists', icon: DollarSign, testId: 'tab-price-lists' },
      { key: 'tolls', label: 'Tolls', icon: Receipt, testId: 'tab-tolls' },
      { key: 'material-types', label: 'Material Types', icon: Layers, testId: 'tab-material-types', hideForCraneHire: true },
    ],
  },
  operations: {
    label: 'Operations',
    icon: Settings2,
    subtabs: [
      { key: 'setup-profiles', label: 'Setup Profiles', icon: Workflow, testId: 'tab-setup-profiles' },
      { key: 'depots', label: 'Depots', icon: MapPin, testId: 'tab-depots', featureFlag: 'depots' },
      { key: 'schedule-folders', label: 'Schedule Folders', icon: Folder, testId: 'tab-schedule-folders', featureFlag: 'schedule_folders' },
      { key: 'cancellation-reasons', label: 'Cancellation Reasons', icon: Ban, testId: 'tab-cancellation-reasons' },
    ],
  },
};

// Map subtab key → parent group key
const SUBTAB_TO_GROUP = {};
for (const [groupKey, group] of Object.entries(GROUPS)) {
  for (const st of group.subtabs) {
    SUBTAB_TO_GROUP[st.key] = groupKey;
  }
}

const DataManagement = () => {
  const { api, user } = useAuth();
  const { hasFeature } = useTier();
  const { depots_enabled: depotsEnabled } = useTenantSettings();
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const [searchParams, setSearchParams] = useSearchParams();

  const isCraneHire = user?.industry?.toLowerCase() === 'crane_hire' || user?.industry?.toLowerCase() === 'crane hire';

  // Determine which subtabs are visible given features/flags
  const isSubtabVisible = (st) => {
    if (st.hideForCraneHire && isCraneHire) return false;
    if (st.featureFlag === 'depots' && !depotsEnabled) return false;
    if (st.featureFlag === 'schedule_folders' && !isFeatureEnabled('schedule_folders')) return false;
    if (st.feature === 'worker_availability' && !hasFeature('worker_availability')) return false;
    return true;
  };

  // Determine which parent groups have at least one visible subtab
  const visibleGroups = Object.entries(GROUPS).filter(([, group]) =>
    group.subtabs.some(isSubtabVisible)
  );

  // Resolve initial active group + subtab from URL
  const initialSubtab = searchParams.get('tab') || 'equipment-types';
  const initialGroup = SUBTAB_TO_GROUP[initialSubtab] || 'assets';

  const [activeGroup, setActiveGroup] = useState(initialGroup);
  const [activeSubtab, setActiveSubtab] = useState(initialSubtab);
  const [loading, setLoading] = useState(true);

  // Shared data state
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [equipmentMakes, setEquipmentMakes] = useState([]);
  const [equipmentModels, setEquipmentModels] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState([]);
  const [complianceTypes, setComplianceTypes] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [setupProfiles, setSetupProfiles] = useState([]);
  const [tolls, setTolls] = useState([]);
  const [taxSettings, setTaxSettings] = useState({ tax_name: 'GST', tax_rate: 10, currency_symbol: '$' });

  // Fetch functions
  const fetchEquipmentTypes = async () => {
    try { const r = await api.get('/equipment-types/all'); setEquipmentTypes(r.data); }
    catch (e) { console.error('Failed to fetch equipment types', e); }
    finally { setLoading(false); }
  };
  const fetchEquipmentMakes = async () => {
    try { const r = await api.get('/equipment-makes'); setEquipmentMakes(r.data); } catch (e) { console.error(e); }
  };
  const fetchEquipmentModels = async () => {
    try { const r = await api.get('/equipment-models'); setEquipmentModels(r.data); } catch (e) { console.error(e); }
  };
  const fetchAttachmentTypes = async () => {
    try { const r = await api.get('/attachment-types/all'); setAttachmentTypes(r.data); } catch (e) { console.error(e); }
  };
  const fetchComplianceTypes = async () => {
    try { const r = await api.get('/compliance-types/all'); setComplianceTypes(r.data); } catch (e) { console.error(e); }
  };
  const fetchWorkerTypes = async () => {
    try { const r = await api.get('/worker-types/all'); setWorkerTypes(r.data); } catch (e) { console.error(e); }
  };
  const fetchPriceLists = async () => {
    try { const r = await api.get('/price-lists'); setPriceLists(r.data); } catch (e) { console.error(e); }
  };
  const fetchMaterialTypes = async () => {
    try { const r = await api.get('/material-types'); setMaterialTypes(r.data); } catch (e) { console.error(e); }
  };
  const fetchSetupProfiles = async () => {
    try { const r = await api.get('/setup-profiles'); setSetupProfiles(r.data); } catch (e) { console.error(e); }
  };
  const fetchTolls = async () => {
    try { const r = await api.get('/tolls'); setTolls(r.data); } catch (e) { console.error(e); }
  };
  const fetchTaxSettings = async () => {
    try { const r = await api.get('/tenant/tax-settings'); setTaxSettings(r.data); } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchEquipmentTypes(); fetchEquipmentMakes(); fetchEquipmentModels();
    fetchAttachmentTypes(); fetchComplianceTypes(); fetchWorkerTypes();
    fetchPriceLists(); fetchMaterialTypes(); fetchTaxSettings();
    fetchSetupProfiles(); fetchTolls();
  }, [api]);

  const handleGroupChange = (groupKey) => {
    setActiveGroup(groupKey);
    const group = GROUPS[groupKey];
    const firstVisible = group.subtabs.find(isSubtabVisible);
    if (firstVisible) {
      setActiveSubtab(firstVisible.key);
      setSearchParams({ tab: firstVisible.key });
    }
  };

  const handleSubtabChange = (subtabKey) => {
    setActiveSubtab(subtabKey);
    setSearchParams({ tab: subtabKey });
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  const currentGroup = GROUPS[activeGroup];
  const visibleSubtabs = currentGroup?.subtabs.filter(isSubtabVisible) || [];

  return (
    <div className="space-y-6" data-testid="data-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Data Management</h1>
          <p className="text-muted-foreground">Manage system lookup data and configurations</p>
        </div>
      </div>

      {/* Parent Group Tabs */}
      <Tabs value={activeGroup} onValueChange={handleGroupChange}>
        <TabsList className="flex-wrap h-auto gap-1" data-testid="parent-tabs">
          {visibleGroups.map(([key, group]) => {
            const Icon = group.icon;
            return (
              <TabsTrigger key={key} value={key} data-testid={`parent-tab-${key}`} className="text-xs sm:text-sm">
                <Icon className="h-4 w-4 mr-1.5" />
                {group.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Sub-tabs for active group */}
      {visibleSubtabs.length > 1 && (
        <div className="flex flex-wrap gap-1.5" data-testid="sub-tabs">
          {visibleSubtabs.map((st) => {
            const Icon = st.icon;
            const isActive = activeSubtab === st.key;
            const isLocked = st.feature && !hasFeature(st.feature);
            return (
              <button
                key={st.key}
                onClick={() => handleSubtabChange(st.key)}
                data-testid={st.testId}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors
                  ${isActive
                    ? 'bg-[#ffd108] text-black font-medium'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {st.label}
                {isLocked && <Lock className="h-3 w-3 ml-0.5 text-muted-foreground" />}
              </button>
            );
          })}
        </div>
      )}

      {/* Tab Content */}
      <div className="mt-2">
        {activeSubtab === 'equipment-types' && (
          <AssetTypesTab api={api} equipmentTypes={equipmentTypes} fetchEquipmentTypes={fetchEquipmentTypes} setupProfiles={setupProfiles} />
        )}
        {activeSubtab === 'makes-models' && (
          <AssetMakesModelsTab api={api} makes={equipmentMakes} models={equipmentModels} fetchMakes={fetchEquipmentMakes} fetchModels={fetchEquipmentModels} />
        )}
        {activeSubtab === 'attachment-types' && (
          <AttachmentTypesTab api={api} attachmentTypes={attachmentTypes} equipmentTypes={equipmentTypes} fetchAttachmentTypes={fetchAttachmentTypes} />
        )}
        {activeSubtab === 'compliance-types' && (
          <ComplianceTypesTab api={api} complianceTypes={complianceTypes} fetchComplianceTypes={fetchComplianceTypes} />
        )}
        {activeSubtab === 'worker-types' && (
          <WorkerTypesTab api={api} workerTypes={workerTypes} fetchWorkerTypes={fetchWorkerTypes} hasFeature={hasFeature} />
        )}
        {activeSubtab === 'price-lists' && (
          <PriceListsTab api={api} priceLists={priceLists} equipmentTypes={equipmentTypes} attachmentTypes={attachmentTypes} workerTypes={workerTypes} taxSettings={taxSettings} fetchPriceLists={fetchPriceLists} />
        )}
        {activeSubtab === 'setup-profiles' && (
          <SetupProfilesTab api={api} equipmentTypes={equipmentTypes} attachmentTypes={attachmentTypes} />
        )}
        {activeSubtab === 'tolls' && (
          <TollsTab api={api} tolls={tolls} fetchTolls={fetchTolls} currencySymbol={taxSettings.currency_symbol || '$'} />
        )}
        {activeSubtab === 'material-types' && !isCraneHire && (
          <MaterialTypesTab api={api} materialTypes={materialTypes} fetchMaterialTypes={fetchMaterialTypes} />
        )}
        {activeSubtab === 'leave-types' && hasFeature('worker_availability') && (
          <LeaveTypesTab api={api} />
        )}
        {activeSubtab === 'depots' && depotsEnabled && (
          <DepotsTab api={api} />
        )}
        {activeSubtab === 'schedule-folders' && isFeatureEnabled('schedule_folders') && (
          <ScheduleFoldersTab api={api} equipmentTypes={equipmentTypes} />
        )}
        {activeSubtab === 'cancellation-reasons' && (
          <CancellationReasonsTab api={api} />
        )}
        {activeSubtab === 'allowance-types' && (
          <AllowanceTypesTab api={api} />
        )}
      </div>
    </div>
  );
};

export default DataManagement;
